import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { strings } from '/imports/strings';

export interface Message extends Doc {
  _id: string;
  conversationId: string;
  senderUserId: string;
  senderAccountId?: string;
  senderName: string;
  senderOrgName: string;
  content: string;
  readByOrgIds?: string[];
}
export type Model = Message;

export const Messages = new Mongo.Collection<Message>('messages');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Messages,
  editableFields: ['conversationId', 'senderUserId', 'senderAccountId', 'senderName', 'senderOrgName', 'content'],
  listMethod: DeclareMethod<ListMethodArg<Message>, ListMethodResponse<Message, Refs>>('listMessages'),
  getMethod: DeclareMethod<string, GetMethodResponse<Message, Refs>>('getMessage'),
  createMethod: DeclareMethod<Partial<Message>, string>('createMessage'),
  updateMethod: DeclareMethod<Partial<Message>, string>('updateMessage'),
  deleteMethod: DeclareMethod<string>('deleteMessage'),
  pathPrefix: '/messages',
  asString: (document) => document.content,
  modelName: strings.Message(),
  pluralName: strings.Messages(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { content: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (message: Partial<Message>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, message, 'conversationId', { required: true });
    validateString(errors, message, 'senderUserId', { required: true });
    validateString(errors, message, 'senderName', { required: true });
    validateString(errors, message, 'senderOrgName', { required: true });
    validateString(errors, message, 'content', { required: true });
    return errors;
  },
};

export interface MessageReceipt extends Doc {
  _id: string;
  conversationId: string;
  messageId: string;
  userId: string;
  snippet?: string;
  readAt?: Date;
  notifiedAt?: Date;
}
export const MessageReceipts = new Mongo.Collection<MessageReceipt>('messageReceipt');

export const markConversationMessagesRead = DeclareMethod<string>('markConversationMessagesRead');

Meteor.isServer && Meteor.startup(async () => {
  await Messages.createIndexAsync({ conversationId: 1 });
  await MessageReceipts.createIndexAsync({ userId: 1 });
  await MessageReceipts.createIndexAsync({ readAt: 1 }, { sparse: true });
  await MessageReceipts.createIndexAsync({ notified: 1 }, { sparse: true });
});